
import * as React from 'react';
import { Layer } from 'office-ui-fabric-react/lib/Layer';
import * as AlertStyles from './Alert.scss';
import { wait } from '../../../utilities/async/wait';
import { IWithWizardInjectedProps, withWizard, IDecorateWithWizardProps } from '../../decorators/withWizard';
import { asAutoAction, IDecorateAsAutoActionProps } from '../../decorators/asAutoAction';

const ANNOUNCE_WAIT_DURATION = 30000;

/**
 * @beta
 */
export interface IAlertBaseProps extends IWithWizardInjectedProps {
  // Nothing.
}

interface IAlertState {
  mayActivate?: boolean;
  mayAnnounce?: boolean;
}

/**
 * Renders an accessible alert for the screen reader, ensuring that the message is set up
 * and handled in a way that screen readers can queue properly for announcement.
 */
class AlertBase extends React.PureComponent<IAlertBaseProps, IAlertState> {
  constructor(props: IAlertBaseProps) {
    super(props);

    this.state = {};
  }

  public render(): JSX.Element {
    return (
      <Layer>
        {
          this.state.mayActivate ? (
            // Screen readers need to see the alert element added to something within the body
            // that was previously there.
            <div
              role='alert'
              className={ AlertStyles.alert }
              style={ {
                // Screen readers need to see the 'alert' element become visible, with its content already present.
                visibility: this.state.mayAnnounce ? 'visible' : 'hidden'
              } }
            >
              { this.props.children }
            </div>
          ) : null
        }
      </Layer>
    );
  }

  public async componentDidMount(): Promise<void> {
    await wait(0);

    this.setState({
      mayActivate: true
    });
  }

  public async componentDidUpdate(previousProps: IAlertBaseProps, previousState: IAlertState): Promise<void> {
    const {
      resolve
    } = this.props;

    if (!previousState.mayAnnounce && this.state.mayAnnounce) {
      await wait(ANNOUNCE_WAIT_DURATION);

      resolve();
    } else if (!previousState.mayActivate && this.state.mayActivate) {
      await wait(0);

      this.setState({
        mayAnnounce: true
      });
    }
  }
}

/**
 * @beta
 */
export type IAlertProps = IDecorateAsAutoActionProps<IDecorateWithWizardProps<IAlertBaseProps>>;

/**
 * @beta
 */
export const Alert: React.ComponentType<IAlertProps> =
  asAutoAction()(withWizard(undefined, { isBackgroundTask: true, backgroundTaskScope: 'headless' })(AlertBase));
